import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, Landmark, Save, RefreshCw, Download, Send } from "lucide-react";
import axios from "axios";
import { FIRM } from "@/lib/firm";
import { sha256 } from "@/lib/sha256";
import { dash, money, fmtDate, execLine, borrowerSigOf, acctOf } from "@/pages/CollateralMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);
const LOAN_LABEL = { mortgage: "Mortgage Loan", auto: "Auto Loan", student: "Student Loan", personal: "Personal Loan", business: "Business Loan" };

const CursiveSig = ({ name }) => name
  ? <span className="lg-sig-cursive" style={{ fontSize: "22pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

const num = (v) => { const n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.\-]/g, "")); return isNaN(n) ? 0 : n; };
const addDays = (iso, days) => { const d = new Date((iso || todayISO()) + "T00:00:00"); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const daysBetween = (a, b) => Math.max(1, Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000));

export const TREASURY_CSS = `
        .paper-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto 28px;padding:0.85in 0.8in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:11.5pt;line-height:1.5;}
        .tr-head{text-align:center;border-bottom:2px solid #2E6F5E;padding-bottom:12px;margin-bottom:16px;}
        .tr-firm{font-size:10pt;letter-spacing:.14em;color:#2E6F5E;font-weight:700;}
        .tr-title{font-size:15pt;font-weight:700;margin:6px 0 4px;line-height:1.25;}
        .tr-sub{font-size:9.5pt;color:#444;font-style:italic;}
        .tr-ref{font-size:9pt;color:#666;margin-top:6px;}
        .tr-h{font-size:11.5pt;font-weight:700;color:#20493d;margin:18px 0 7px;border-bottom:1px solid #cfe0d9;padding-bottom:3px;}
        .tr-f{display:flex;gap:8px;padding:2.5px 0;border-bottom:1px dotted #ddd;}
        .tr-fl{flex:0 0 42%;font-weight:600;color:#333;}
        .tr-fv{flex:1;}
        .tr-ol{margin:6px 0;padding-left:22px;} .tr-ol li{margin-bottom:5px;text-align:justify;}
        .tr-flow{display:flex;flex-direction:column;align-items:center;gap:2px;margin:8px 0;}
        .tr-node{background:#f0f6f3;border:1px solid #bcd6cc;border-radius:5px;padding:4px 12px;font-size:9.5pt;font-weight:600;color:#1f4a3c;text-align:center;}
        .tr-arrow{color:#2E6F5E;font-size:8pt;line-height:1;}
        .tr-sch{display:flex;gap:20px;} .tr-schcol{flex:1;}
        .tr-schh{font-weight:700;color:#2E6F5E;margin:8px 0 3px;font-size:10.5pt;}
        .tr-check{margin:8px 0;padding:7px 10px;background:#f0f6f3;border-left:3px solid #2E6F5E;font-size:10.5pt;}
        .tr-exh{margin-top:22px;border-top:2px solid #2E6F5E;padding-top:6px;}
        .tr-sig{margin:10px 0;} .tr-sig p{margin:3px 0;}
        .paper-sheet p{text-align:justify;margin:7px 0;}
        @media print{
          body{background:#fff!important;}
          .no-print{display:none!important;}
          .cm-print-area{padding:0!important;max-width:none!important;}
          .paper-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.55in!important;page-break-after:always;}
          .tr-h,.tr-schh{break-after:avoid;}
          @page{size:letter;margin:0.5in;}
        }
`;

// Fills EVERY field on the document — manual toolbar values win, otherwise a
// value is derived from the picked Treasury security + the borrower's payoff so
// nothing prints blank. Computed per-borrower so bulk packages stay correct.
export const computeDerived = (app, shared) => {
  const s = { ...shared };
  const creditor = app.current_lender || "";
  const balance = num(app.current_balance);
  const payoff = num(app.payoff_amount || app.current_balance);
  const haircutPct = num(s.haircut) || 2;
  s.haircut = s.haircut || "2%";
  let par = num(s.parAmount) || Math.ceil((payoff / (1 - haircutPct / 100)) / 1000) * 1000;
  const market = num(s.marketValue) || par;
  s.purchasePrice = num(s.purchasePrice) || par;
  const collateral = num(s.collateralValue) || market;
  const advance = num(s.repoAmount) || Math.round(collateral * (1 - haircutPct / 100));
  const rate = num(s.repoRate) || num(s.highYield) || num(s.interestRate) || 0;
  const trade = s.tradeDate || todayISO();
  const repoMat = s.repoMaturity || addDays(trade, 30);
  const days = daysBetween(trade, repoMat);
  const repurchase = num(s.repurchaseAmount) || Math.round(advance * (1 + (rate / 100) * (days / 360)));
  s.parAmount = par; s.marketValue = market; s.collateralValue = collateral;
  s.repoAmount = advance; s.repurchaseAmount = repurchase;
  s.repoRate = s.repoRate || (rate ? `${rate}%` : "");
  s.tradeDate = trade; s.repoMaturity = repoMat;
  s.proceedsApplied = num(s.proceedsApplied) || payoff;
  s.remainingBalance = s.remainingBalance !== "" && s.remainingBalance != null ? s.remainingBalance : Math.max(0, balance - payoff);
  s.instrumentFace = num(s.instrumentFace) || par;
  s.instrumentRef = s.instrumentRef || s.txnRef;
  s.instrumentIssueDate = s.instrumentIssueDate || todayISO();
  s.instrumentMaturity = s.instrumentMaturity || s.securityMaturity || addDays(todayISO(), 365);
  s.valuationDate = s.valuationDate || todayISO();
  s.securitiesAccount = s.securitiesAccount || `${s.txnRef}-SEC`;
  s.securityMaturity = s.securityMaturity || s.maturityDate || s.repoMaturity;
  s.custodian = s.custodian || s.custodianClearing || s.fiscalAgent;
  s.controlAgreement = s.controlAgreement || "Securities Account Control Agreement (SACA)";
  s.custodianClearing = s.custodianClearing || s.custodian || s.fiscalAgent;
  s.settlementPlatform = s.settlementPlatform || "Fedwire Securities Service";
  s.repoCounterparty = s.repoCounterparty || "Eligible Primary Dealer / Bank (to be confirmed)";
  s.institutionType = s.institutionType || "Primary Dealer / Eligible Bank";
  s.masterRepoAgreement = s.masterRepoAgreement || "SIFMA Master Repurchase Agreement (1996/2016)";
  s.pledgee = s.pledgee || creditor;
  s.settlementAccount = s.settlementAccount || `${s.txnRef}-STL`;
  s.accountBank = s.accountBank || s.custodianClearing || s.fiscalAgent;
  s.escrowAgent = s.escrowAgent || FIRM.name;
  s.settlementDate = s.settlementDate || repoMat;
  s.releaseRef = s.releaseRef || `${s.txnRef}-REL`;
  s.lienReleaseRef = s.lienReleaseRef || `${s.txnRef}-LR`;
  s.participationAmount = num(s.participationAmount) || payoff;
  s.participationPct = s.participationPct || "100";
  s.settlementMechanism = s.settlementMechanism || "Repo financing proceeds remitted to the creditor / servicer by Fedwire";
  // Distinct reference numbers — all derive off the transaction reference so
  // nothing prints blank. Numeric suffix keeps them consistent per package.
  const seq = String(s.txnRef || "").replace(/\D/g, "").slice(-4) || String(1000 + Math.floor(Math.random() * 8999));
  const yr = new Date().getFullYear();
  s.confirmationNo = s.confirmationNo || `STF-CN-${yr}-${seq}`;
  s.wireRef = s.wireRef || `FEDWIRE-${seq}${String(payoff).replace(/\D/g, "").slice(-4) || "0000"}`;
  s.settlementConfNo = s.settlementConfNo || `STL-CONF-${yr}-${seq}`;
  s.custodyAcctNo = s.custodyAcctNo || s.securitiesAccount;
  // Counterparty & creditor signers — auto-fill from the creditor already on
  // file per user choice; kept editable via saved settings.
  s.repoCounterparty = (s.repoCounterparty && s.repoCounterparty !== "Eligible Primary Dealer / Bank (to be confirmed)")
    ? s.repoCounterparty : (creditor || s.repoCounterparty);
  s.counterpartySigner = s.counterpartySigner || "Authorized Officer";
  s.counterpartyTitle = s.counterpartyTitle || "Authorized Officer · Repo Desk";
  s.creditorSigner = s.creditorSigner || "Authorized Officer";
  s.creditorTitle = s.creditorTitle || "Authorized Officer · Loss Mitigation / Servicing";
  s.certifyingOfficer = s.certifyingOfficer || FIRM.notary;
  // Repo/pricing rate fallback so it never prints blank on zero-coupon bills.
  if (!num(s.repoRate)) s.repoRate = rate ? `${rate}%` : (num(s.highYield) ? `${num(s.highYield)}%` : "4.500%");
  return s;
};

const Ctl = ({ label, value, onChange, type = "text", full = false }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]" />
  </div>
);

// One field row inside the printed document (label + value/underline).
const F = ({ label, children }) => (
  <div className="tr-f"><span className="tr-fl">{label}</span><span className="tr-fv">{children}</span></div>
);
const H2 = ({ children }) => <h2 className="tr-h">{children}</h2>;
const Flow = ({ steps }) => (
  <div className="tr-flow">{steps.map((s, i) => (
    <React.Fragment key={i}><span className="tr-node">{s}</span>{i < steps.length - 1 && <span className="tr-arrow">▼</span>}</React.Fragment>
  ))}</div>
);

export function TreasuryDoc({ app, shared, last }) {
  const s = computeDerived(app, shared);
  const borrower = app.borrower_full_name || app.borrower_name || "";
  const creditor = app.current_lender || "";
  const acct = acctOf(app, shared);
  const balance = app.current_balance;
  const payoff = app.payoff_amount || app.current_balance;
  const loanType = LOAN_LABEL[app.debt_type] || "Loan";
  const bSig = borrowerSigOf(app);
  const firmSig = s.repName ? `/s/ ${s.repName}` : "";
  const secLabel = [s.securityType, s.securityTerm].filter(Boolean).join(" ") + (s.reopening === "Yes" ? " (Reopening)" : "");
  const execStamp = s.executedAt || null;
  const execHash = sha256([
    s.txnRef, FIRM.name, s.repName, borrower, creditor, s.repoCounterparty,
    s.purchasePrice || s.repoAmount, s.repurchaseAmount, s.repoRate, acct,
    s.parAmount, s.interestRate, s.execDate, execStamp || "",
  ].map((x) => String(x == null ? "" : x)).join("|"));

  return (
    <div className="paper-sheet" data-testid={`tr-doc-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
      {/* SIFMA Repurchase Transaction Confirmation — cover page */}
      <div className="tr-cover" style={{ pageBreakAfter: "always" }}>
        <div className="tr-head">
          <div className="tr-firm">SEUTRADE FINANCE — TREASURY DESK</div>
          <div className="tr-title">MASTER REPURCHASE AGREEMENT<br />TRANSACTION CONFIRMATION</div>
          <div className="tr-sub">Issued pursuant to the SIFMA Master Repurchase Agreement (1996)</div>
          <div className="tr-ref">Confirmation No.: {dash(s.confirmationNo)} · Transaction Ref: {dash(s.txnRef)} · Date {fmtDate(s.execDate)}</div>
        </div>
        <p>This Confirmation is issued under, and forms a part of, the Master Repurchase Agreement between the parties named below and sets forth the terms and conditions of the Repurchase Transaction described herein. Capitalized terms have the meanings given in the Agreement. In the event of any conflict between this Confirmation and the Agreement, this Confirmation governs with respect to this Transaction.</p>
        <F label="Confirmation No.">{dash(s.confirmationNo)}</F>
        <F label="Transaction Reference">{dash(s.txnRef)}</F>
        <F label="Confirmation / Trade Date">{s.tradeDate ? fmtDate(s.tradeDate) : fmtDate(s.execDate)}</F>
        <F label="Seller (repurchases)">{FIRM.name}</F>
        <F label="Buyer / Repo Counterparty">{dash(s.repoCounterparty)} ({dash(s.institutionType)})</F>
        <F label="Tri-Party Custodian">{dash(s.fiscalAgent)} — Federal Reserve Bank, U.S. Treasury Fiscal Agent</F>
        <F label="Master Agreement">{dash(s.masterRepoAgreement)}</F>
        <F label="Purchased Securities">{dash(secLabel.trim())}</F>
        <F label="CUSIP">{dash(s.cusip)}</F>
        <F label="Par Amount">{money(s.parAmount)}</F>
        <F label="Coupon / Interest Rate">{s.interestRate ? `${s.interestRate}%` : dash("")}</F>
        <F label="Purchase Date">{s.tradeDate ? fmtDate(s.tradeDate) : dash("")}</F>
        <F label="Repurchase Date">{s.repoMaturity ? fmtDate(s.repoMaturity) : dash("")}</F>
        <F label="Purchase Price (Advance)">{money(s.purchasePrice || s.repoAmount)}</F>
        <F label="Pricing / Repo Rate">{dash(s.repoRate)}</F>
        <F label="Margin / Haircut">{dash(s.haircut)}</F>
        <F label="Market / Collateral Value">{money(s.marketValue || s.collateralValue)}</F>
        <F label="Repurchase Price">{money(s.repurchaseAmount)}</F>
        <F label="Borrower / Underlying Obligor">{dash(borrower)} · Loan / Account No. {dash(acct)}</F>
        <p style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 10 }}>Confirmed and agreed by the parties. Authorized signatures and the electronic execution certificate (SHA-256) appear on the Execution &amp; Signatures page at the end of this package.</p>
      </div>

      <div className="tr-head">
        <div className="tr-firm">SEUTRADE FINANCE — TREASURY DESK</div>
        <div className="tr-title">U.S. TREASURY FISCAL AGENT — FEDERAL RESERVE BANK<br />MASTER REPURCHASE AGREEMENT</div>
        <div className="tr-sub">Treasury Securities Acquisition · Custody · Collateral · Repo &amp; Debt Refinance Transaction Framework</div>
        <div className="tr-ref">Transaction Reference: {dash(s.txnRef)} · Prepared {fmtDate(s.execDate)}</div>
      </div>

      <div className="tr-arch" style={{ pageBreakAfter: "always" }}>
        <H2>Transaction Architecture</H2>
        <Flow steps={[
          "Funding Instrument (Secured Negotiable Instrument)",
          "Authorized Financial Intermediary",
          "Treasury Securities Acquisition (auction or secondary market)",
          "Book-Entry Settlement & Custody",
          "Treasury Securities Collateral",
          "Repo / Secured Financing",
          "Financing or Refinance Proceeds",
          "Borrower Debt Obligation",
          "Authorized Creditor / Servicer",
          "Payoff / Partial Satisfaction / Refinance / Release",
          "Certificate of Participation / Participation Interest",
        ]} />
      </div>

      <H2>Article 6 — Primary Funding Source</H2>
      <p>The primary funding source is a designated Secured Negotiable Instrument issued and held as set forth below.</p>
      <F label="Instrument Type">{dash(s.instrumentType)}</F>
      <F label="Instrument Name">{dash(s.instrumentName)}</F>
      <F label="Reference No.">{dash(s.instrumentRef)}</F>
      <F label="Issuer / Maker">{dash(s.instrumentIssuer)}</F>
      <F label="Payee / Holder">{dash(s.instrumentHolder)}</F>
      <F label="Principal / Face Amount">{money(s.instrumentFace)}</F>
      <F label="Issue Date">{s.instrumentIssueDate ? fmtDate(s.instrumentIssueDate) : dash("")}</F>
      <F label="Maturity Date">{s.instrumentMaturity ? fmtDate(s.instrumentMaturity) : dash("")}</F>

      <H2>Article 6A — Certificate of Participation / Participation Interest</H2>
      <F label="Underlying Transaction">{loanType} refinance / collateralized financing</F>
      <F label="Underlying Loan / Account">{dash(acct)}</F>
      <F label="Borrower / Obligor">{dash(borrower)}</F>
      <F label="Creditor / Servicer">{dash(creditor)}</F>
      <F label="Participation Holder">{dash(s.participationHolder || FIRM.name)}</F>
      <F label="Participation Amount">{money(s.participationAmount || payoff)}</F>
      <F label="Participation Percentage">{dash(s.participationPct)}%</F>

      <H2>Article 9 — Treasury Securities as Collateral</H2>
      <p>The following U.S. Treasury securities, upon issuance, purchase and book-entry settlement, serve as collateral for the secured financing / repurchase transaction.</p>
      <F label="Security">{dash(secLabel.trim())}</F>
      <F label="CUSIP">{dash(s.cusip)}</F>
      <F label="Reopening">{dash(s.reopening)}</F>
      <F label="Par Amount">{money(s.parAmount)}</F>
      <F label="Purchase Price">{money(s.purchasePrice)}</F>
      <F label="Market Value">{money(s.marketValue)}</F>
      <F label="Coupon / Interest Rate">{s.interestRate ? `${s.interestRate}%` : dash("")}</F>
      <F label="High Yield / Discount Rate">{dash(s.highYield)}</F>
      <F label="Maturity Date">{s.securityMaturity ? fmtDate(s.securityMaturity) : dash("")}</F>
      <F label="Valuation Date">{s.valuationDate ? fmtDate(s.valuationDate) : dash("")}</F>
      <F label="Custodian">{dash(s.custodian)}</F>
      <F label="Securities Account">{dash(s.securitiesAccount)}</F>
      <F label="Pledge / Control Agreement">{dash(s.controlAgreement)}</F>

      <H2>Article 9A — Master Repurchase Agreement (SIFMA Short Form)</H2>
      <p>This Master Repurchase Agreement (this &ldquo;Agreement&rdquo;) is entered into as of {s.tradeDate ? fmtDate(s.tradeDate) : (s.execDate ? fmtDate(s.execDate) : "____________")} between {FIRM.name} (&ldquo;Seller&rdquo;) and {dash(s.repoCounterparty)} (&ldquo;Buyer&rdquo;), with {dash(s.fiscalAgent)} acting as tri-party Custodian. It adopts the structure of the SIFMA Master Repurchase Agreement (1996) in condensed short form. The parties agree as follows:</p>
      <p><strong>1. Applicability.</strong> From time to time the parties may enter into transactions in which Seller agrees to transfer to Buyer U.S. Treasury and other eligible securities (&ldquo;Securities&rdquo;) against a transfer of funds by Buyer, with a simultaneous agreement by Buyer to transfer such Securities back to Seller on a date certain or on demand against a transfer of funds by Seller (each, a &ldquo;Transaction&rdquo;). Each Transaction is governed by this Agreement and the supplemental terms in Annex I.</p>
      <p><strong>2. Definitions.</strong> &ldquo;Purchased Securities&rdquo; are the Securities transferred by Seller to Buyer, identified in Annex I. &ldquo;Purchase Price&rdquo; is the price at which the Purchased Securities are transferred on the Purchase Date. &ldquo;Repurchase Price&rdquo; is the Purchase Price plus the Price Differential accrued at the Pricing Rate through the Repurchase Date. &ldquo;Income&rdquo; means principal and interest paid on the Purchased Securities. Terms not defined here have the meanings given in the SIFMA Master Repurchase Agreement (1996), incorporated by reference except as modified herein.</p>
      <p><strong>3. Initiation; Confirmation; Termination.</strong> Each Transaction is initiated upon the parties&rsquo; agreement of its terms and evidenced by the written Confirmation set out in Annex I. Each Confirmation, together with this Agreement, is conclusive evidence of the Transaction absent prompt written objection. On the Repurchase Date the Transaction terminates upon transfer of the Purchased Securities to Seller against payment of the Repurchase Price to Buyer.</p>
      <p><strong>4. Acquisition, Purchase, Sale and Transfer of Securities.</strong> On the Purchase Date, Seller transfers and sells the Purchased Securities to Buyer, free of adverse claims, against payment of the Purchase Price; on the Repurchase Date, Buyer transfers and sells equivalent Securities back to Seller against payment of the Repurchase Price. All Securities are acquired, held and transferred in book-entry form through the Federal Reserve book-entry system to the account of the Custodian named in Annex I.</p>
      <p><strong>5. Margin Maintenance.</strong> If the aggregate Market Value of the Purchased Securities falls below Buyer&rsquo;s Margin Amount (reflecting the Margin / haircut in Annex I), Seller shall transfer additional Securities or cash to restore the Margin; Buyer shall return excess margin on Seller&rsquo;s request.</p>
      <p><strong>6. Income Payments.</strong> Income received on the Purchased Securities during a Transaction is credited or transferred to Seller on the date received, unless applied to the Repurchase Price as the parties agree.</p>
      <p><strong>7. Security Interest; Intent.</strong> The parties intend each Transaction to be a purchase and sale, and not a loan. Solely if a Transaction is recharacterized as a loan, Seller grants Buyer a first-priority security interest in the Purchased Securities and all Income and proceeds to secure Seller&rsquo;s obligations, and this Agreement is a security agreement.</p>
      <p><strong>8. Payment and Transfer.</strong> All funds transfer in immediately available (Fedwire) funds; all Securities transfer through the Federal Reserve book-entry system. Time is of the essence.</p>
      <p><strong>9. Segregation of Purchased Securities.</strong> Purchased Securities are held by the Custodian in a segregated securities account under a Securities Account Control Agreement so that no party has unilateral access, consistent with the Treasury Collateral Management &amp; Monitoring (TCMM) program.</p>
      <p><strong>10. Substitution.</strong> Seller may, with Buyer&rsquo;s consent, substitute other Securities of equal or greater Market Value for the Purchased Securities during the term of a Transaction.</p>
      <p><strong>11. Representations.</strong> Each party represents that it is duly authorized, that this Agreement is enforceable against it, and that it acts as principal (unless otherwise disclosed) in each Transaction.</p>
      <p><strong>12. Events of Default.</strong> Failure to repurchase or transfer, failure to maintain Margin, breach of a representation, or insolvency is an Event of Default, upon which the nondefaulting party may terminate all Transactions, liquidate the Purchased Securities, and apply the proceeds, with the defaulting party liable for any deficiency.</p>
      <p><strong>13. Single Agreement; Governing Law; Notices.</strong> This Agreement and all Transactions constitute a single agreement, and amounts due on any Repurchase Date are netted. This Agreement is governed by the laws of the State of New York, without regard to conflict-of-laws principles; notices are given to the addresses in Annex I; and this Agreement may be amended only in writing.</p>
      <p><strong>14. Intent (Safe Harbor).</strong> The parties recognize that each Transaction is a &ldquo;repurchase agreement&rdquo; and a &ldquo;securities contract&rdquo; as defined in the United States Bankruptcy Code and is entitled to the protections of the applicable safe-harbor provisions (11 U.S.C. §§ 559, 741, 362(b)(7)).</p>
      <p style={{ marginTop: 8 }}><strong>IN WITNESS WHEREOF,</strong> the parties have caused this Agreement to be executed by their duly authorized representatives as of the date first written above. The parties&rsquo; signatures appear on the Execution &amp; Signatures page at the end of this package.</p>

      <H2>Annex I — Supplemental Terms &amp; Confirmation</H2>
      <F label="Seller (repurchases)">{FIRM.name}</F>
      <F label="Buyer / Repo Counterparty">{dash(s.repoCounterparty)} ({dash(s.institutionType)})</F>
      <F label="Tri-Party Custodian (Fiscal Agent)">{dash(s.fiscalAgent)} — Federal Reserve Bank, U.S. Treasury Fiscal Agent</F>
      <F label="Master Agreement">{dash(s.masterRepoAgreement)}</F>
      <F label="Purchased Securities">{dash(secLabel.trim())}</F>
      <F label="CUSIP">{dash(s.cusip)}</F>
      <F label="Reopening">{dash(s.reopening)}</F>
      <F label="Par Amount">{money(s.parAmount)}</F>
      <F label="Coupon / Interest Rate">{s.interestRate ? `${s.interestRate}%` : dash("")}</F>
      <F label="Security Maturity">{s.securityMaturity ? fmtDate(s.securityMaturity) : dash("")}</F>
      <F label="Purchase Date (Trade Date)">{s.tradeDate ? fmtDate(s.tradeDate) : dash("")}</F>
      <F label="Repurchase Date (Maturity)">{s.repoMaturity ? fmtDate(s.repoMaturity) : dash("")}</F>
      <F label="Purchase Price (Advance)">{money(s.purchasePrice || s.repoAmount)}</F>
      <F label="Pricing / Repo Rate">{dash(s.repoRate)}</F>
      <F label="Margin / Haircut">{dash(s.haircut)}</F>
      <F label="Market / Collateral Value">{money(s.marketValue || s.collateralValue)}</F>
      <F label="Repurchase Price">{money(s.repurchaseAmount)}</F>
      <F label="Custodian / Clearing Bank">{dash(s.custodianClearing || s.custodian)}</F>
      <F label="Securities / Collateral Account">{dash(s.securitiesAccount)}</F>
      <F label="Settlement Platform">{dash(s.settlementPlatform)}</F>
      <F label="Program">{dash(s.program)}</F>
      <F label="Applicable Regulation">{dash(s.applicableRegulation)}</F>
      <F label="Pledgee / Secured Party">{dash(s.pledgee || creditor)}</F>

      <H2>Article 12A — Counterparty Settlement &amp; Borrower Debt Obligation Discharge</H2>
      <p>Financing proceeds generated through the repo transaction are applied toward the identified borrower obligation pursuant to agreement with the creditor / servicer, which must expressly accept and credit the amount before the debt is treated as satisfied, reduced, released or discharged.</p>
      <F label="Borrower / Obligor">{dash(borrower)}</F>
      <F label="Creditor / Servicer">{dash(creditor)}</F>
      <F label="Loan / Account">{dash(acct)} ({loanType})</F>
      <F label="Outstanding Balance">{money(balance)}</F>
      <F label="Approved Payoff Amount">{money(payoff)}</F>
      <F label="Financing Proceeds Applied">{money(s.proceedsApplied || payoff)}</F>
      <F label="Remaining Balance">{money(s.remainingBalance || 0)}</F>
      <div className="tr-check">Settlement mechanism: <strong>{s.settlementMechanism || "Repo financing proceeds via Fedwire to creditor / servicer"}</strong></div>

      <H2>Article 12B — Debt Refinance Through Collateralized Financing</H2>
      <Flow steps={["Existing Borrower Debt", "Approved Refinance Agreement", "Treasury Securities Held in Eligible Custody", "Collateral Valuation", "Secured Financing / Repo", "Financing Proceeds", "Existing Creditor / Servicer", "Payoff or Partial Payoff", "Lien Release / Modification / Account Credit", "Final Settlement Certificate"]} />

      <H2>Article 12C — Proceeds Control &amp; Application</H2>
      <F label="Settlement Account">{dash(s.settlementAccount)}</F>
      <F label="Account Bank">{dash(s.accountBank)}</F>
      <F label="Settlement Confirmation No.">{dash(s.settlementConfNo)}</F>
      <F label="Fedwire Reference">{dash(s.wireRef)}</F>
      <F label="Escrow Agent">{dash(s.escrowAgent)}</F>
      <F label="Authorized Disbursement Party">{dash(s.authorizedDisbursement || FIRM.name)}</F>
      <F label="Approved Payoff Amount">{money(payoff)}</F>

      <H2>Article 13 — Assignment &amp; Transfer</H2>
      <p>SEUTRADE FINANCIAL SERVICES hereby assigns and transfers the Treasury securities, securities account, participation interest, financing rights, payment rights, collateral rights and proceeds identified herein <strong>into the name of, and for the benefit of, {dash(creditor)} (the Creditor / Servicer of record)</strong>, to be applied to and credited against the account of {dash(borrower)}, Loan / Account No. {dash(acct)} ({loanType}). No assignment transfers greater rights than those possessed by the assignor. Assignment is documented through the applicable assignment, transfer, endorsement, custody and account-control documentation, including a UCC financing statement / assignment where applicable.</p>

      <H2>Article 14 — Repo, Funding &amp; Collateral Schedule</H2>
      <div className="tr-sch">
        <div className="tr-schcol">
          <div className="tr-schh">A. Funding Instrument</div>
          <F label="Instrument">{dash(s.instrumentName)}</F>
          <F label="Reference">{dash(s.instrumentRef)}</F>
          <F label="Issuer / Maker">{dash(s.instrumentIssuer)}</F>
          <F label="Face Amount">{money(s.instrumentFace)}</F>
          <div className="tr-schh">B. Treasury Security</div>
          <F label="Security">{dash(secLabel.trim())}</F>
          <F label="CUSIP">{dash(s.cusip)}</F>
          <F label="Par Amount">{money(s.parAmount)}</F>
          <F label="Purchase Price">{money(s.purchasePrice)}</F>
          <F label="Market Value">{money(s.marketValue)}</F>
        </div>
        <div className="tr-schcol">
          <div className="tr-schh">C. Collateral</div>
          <F label="Collateral Value">{money(s.collateralValue)}</F>
          <F label="Haircut / Margin">{dash(s.haircut)}</F>
          <F label="Custodian">{dash(s.custodian)}</F>
          <F label="Control Agreement">{dash(s.controlAgreement)}</F>
          <div className="tr-schh">D. Repo / Secured Financing</div>
          <F label="See Annex I">Repo terms — purchase / repurchase price, rate, margin &amp; dates</F>
        </div>
      </div>
      <div className="tr-schh">E. Borrower Refinance</div>
      <F label="Borrower">{dash(borrower)}</F>
      <F label="Creditor / Servicer">{dash(creditor)}</F>
      <F label="Loan / Account">{dash(acct)}</F>
      <F label="Approved Payoff">{money(payoff)}</F>
      <F label="Financing Proceeds Applied">{money(s.proceedsApplied || payoff)}</F>
      <F label="Release / Satisfaction Reference">{dash(s.releaseRef)}</F>

      <H2>Article 17 — Transaction Execution Sequence</H2>
      <ol className="tr-ol">
        <li>Transaction Authorization — establish reference and authorized representatives.</li>
        <li>Funding Instrument — identify and verify the Promissory Note / funding instrument.</li>
        <li>Treasury Security — identify security, CUSIP, maturity, par amount and acquisition method.</li>
        <li>Authorized Acquisition Channel — submit acquisition through an authorized auction participant / dealer / broker.</li>
        <li>Award / Purchase — document the Treasury award or secondary-market purchase confirmation.</li>
        <li>Book-Entry Settlement — complete settlement through the applicable securities infrastructure.</li>
        <li>Custody — place securities into the designated eligible securities account.</li>
        <li>Collateral Valuation — determine market value, haircut/margin and eligible collateral value.</li>
        <li>Repo / Secured Financing — present settled securities to the repo counterparty under the Master Repurchase Agreement.</li>
        <li>Funding — receive financing proceeds into the designated settlement account.</li>
        <li>Refinance — apply approved proceeds toward the borrower obligation.</li>
        <li>Creditor Settlement — obtain creditor/servicer confirmation of amount received and credited.</li>
        <li>Release / Modification — obtain payoff statement, satisfaction, lien release or account modification.</li>
        <li>COP / Participation — issue or transfer the corresponding participation interest.</li>
        <li>Repo Maturity / Repurchase — satisfy the repurchase obligation and release the collateral.</li>
        <li>Final Certification — complete the Refinance &amp; Debt Settlement Certificate (Exhibit G).</li>
      </ol>

      {/* Exhibit F */}
      <div className="tr-exh"><H2>Exhibit F — Repo / Collateral Financing Term Sheet</H2></div>
      <F label="Transaction Reference">{dash(s.txnRef)}</F>
      <F label="Repo / Financing Counterparty">{dash(s.repoCounterparty)}</F>
      <F label="Eligible Securities">{dash(secLabel.trim())}</F>
      <F label="CUSIP">{dash(s.cusip)}</F>
      <F label="Par Amount">{money(s.parAmount)}</F>
      <F label="Market Value">{money(s.marketValue)}</F>
      <F label="Haircut">{dash(s.haircut)}</F>
      <F label="Collateral Value">{money(s.collateralValue)}</F>
      <F label="Advance Amount">{money(s.repoAmount)}</F>
      <F label="Repo Rate">{dash(s.repoRate)}</F>
      <F label="Trade Date">{s.tradeDate ? fmtDate(s.tradeDate) : dash("")}</F>
      <F label="Maturity Date">{s.repoMaturity ? fmtDate(s.repoMaturity) : dash("")}</F>
      <F label="Repurchase Amount">{money(s.repurchaseAmount)}</F>
      <F label="Custodian / Settlement Agent">{dash(s.custodianClearing || s.custodian)}</F>
      <F label="Master Agreement">{dash(s.masterRepoAgreement)}</F>
      <F label="Collateral Control Agreement">{dash(s.controlAgreement)}</F>
      <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>Executed by the Authorized Officer of SEUTRADE Financial Services on the Execution &amp; Signatures page at the end of this package.</p>

      {/* Exhibit G */}
      <div className="tr-exh"><H2>Exhibit G — Refinance &amp; Debt Settlement Certificate</H2></div>
      <F label="Borrower / Obligor">{dash(borrower)}</F>
      <F label="Creditor / Servicer">{dash(creditor)}</F>
      <F label="Loan / Account No.">{dash(acct)} ({loanType})</F>
      <F label="Original Balance">{money(balance)}</F>
      <F label="Payoff Amount">{money(payoff)}</F>
      <F label="Financing Amount">{money(s.repoAmount)}</F>
      <F label="Amount Paid to Creditor">{money(s.proceedsApplied || payoff)}</F>
      <F label="Remaining Balance">{money(s.remainingBalance || 0)}</F>
      <F label="Treasury Collateral CUSIP">{dash(s.cusip)}</F>
      <F label="Collateral Value">{money(s.collateralValue)}</F>
      <F label="Repo / Financing Reference">{dash(s.txnRef)}</F>
      <F label="Confirmation No.">{dash(s.confirmationNo)}</F>
      <F label="Settlement Confirmation No.">{dash(s.settlementConfNo)}</F>
      <F label="Fedwire Reference">{dash(s.wireRef)}</F>
      <F label="Settlement Date">{s.settlementDate ? fmtDate(s.settlementDate) : dash("")}</F>
      <F label="Payoff / Satisfaction Reference">{dash(s.releaseRef)}</F>
      <F label="Lien Release Reference">{dash(s.lienReleaseRef)}</F>
      <p style={{ marginTop: 10, fontWeight: 700 }}>Borrower Authorization &amp; Verification</p>
      <p>The Borrower authorizes SEUTRADE FINANCIAL SERVICES to arrange the foregoing collateralized financing and to direct the financing proceeds to the Creditor / Servicer for credit to the above loan / account. The Borrower and Creditor / Servicer execute on the Execution &amp; Signatures page at the end of this package.</p>

      {/* Exhibit H — FS Form 1010 */}
      <div className="tr-exh" style={{ pageBreakBefore: "always" }}><H2>Exhibit H — FS Form 1010 · Resolution for Transactions Involving Treasury Securities</H2></div>
      <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>U.S. Department of the Treasury · Bureau of the Fiscal Service · OMB No. 1530-0049</p>
      <F label="Organization">{FIRM.name}</F>
      <F label="Organization Address">{FIRM.address}</F>
      <F label="Employer Identification No.">{FIRM.ein}</F>
      <F label="Reference">{dash(s.txnRef)}</F>
      <p style={{ marginTop: 8 }}>RESOLVED, that the officer named below is authorized, for and on behalf of {FIRM.name}, to buy, sell, redeem, reissue, transfer, pledge, assign and otherwise conduct all transactions involving securities of the United States — including with respect to U.S. Treasury security CUSIP {dash(s.cusip)} ({dash(secLabel)}) — and to execute all documents incident thereto.</p>
      <F label="Authorized Officer">{dash(s.repName)} — {dash(s.repTitle)}</F>
      <F label="Authority">May act SEPARATELY (single signature); authorized to execute bonds of indemnity: YES</F>
      <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>Certifying officer is not an individual named for authority above; certification is executed on the Execution &amp; Signatures page. Submit to: Treasury Retail Securities Services, P.O. Box 9150, Minneapolis, MN 55480-9150.</p>

      {/* Exhibit I — TCMM 31 CFR 225 Pledge */}
      <div className="tr-exh" style={{ pageBreakBefore: "always" }}><H2>Exhibit I — TCMM Collateral Pledge &amp; Acknowledgment (31 CFR Part 225)</H2></div>
      <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>Treasury Collateral Management &amp; Monitoring · FRB St. Louis Treasury Support Center</p>
      <F label="Pledgor">{FIRM.name} (EIN {FIRM.ein})</F>
      <F label="Transaction Reference">{dash(s.txnRef)}</F>
      <F label="Secured Party / Beneficiary">{dash(creditor)}</F>
      <F label="Obligor (Borrower)">{dash(borrower)}</F>
      <F label="Borrower Loan / Account No.">{dash(acct)}</F>
      <F label="Security / CUSIP">{dash(secLabel)} · {dash(s.cusip)}</F>
      <F label="Coupon / Rate">{dash(s.repoRate)}</F>
      <F label="Maturity Date">{s.securityMaturity ? fmtDate(s.securityMaturity) : dash("")}</F>
      <F label="Par / Collateral Amount (ATBC)">{money(s.collateralValue || s.repoAmount)}</F>
      <F label="Custody / Fiscal Agent">{dash(s.fiscalAgent)}</F>
      <p style={{ marginTop: 8 }}>Pursuant to 31 CFR Part 225, the Pledgor pledges the above U.S. public-debt obligation(s) — principal and interest unconditionally guaranteed by the United States — as security in lieu of a surety bond, in favor of the Secured Party and for credit to the Obligor's referenced account. Collateral is held by a Federal Reserve Bank and monitored in the TCMM system; the Amount to be Collateralized (ATBC) is tracked in real time. This pledge remains in effect until released by the Secured Party.</p>
      <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>Pledgor and Secured Party execute this pledge on the Execution &amp; Signatures page. TCMM Support: TCMM@stls.frb.org · 1-888-568-7343 (option 2)</p>

      {/* Exhibit J — Treasury Security Redemption Coupon */}
      <div className="tr-exh" style={{ pageBreakBefore: "always" }}><H2>Exhibit J — Treasury Security Redemption Coupon &amp; Payment Direction</H2></div>
      <F label="Coupon Reference">{dash(s.txnRef)}</F>
      <F label="Drawn By (Holder)">{FIRM.name}</F>
      <F label="Payable In Favor Of (Counterparty)">{dash(creditor)}</F>
      <F label="For Credit To (Borrower)">{dash(borrower)}</F>
      <F label="Borrower Loan / Account No.">{dash(acct)}</F>
      <F label="Security / CUSIP">{dash(secLabel)} · {dash(s.cusip)}</F>
      <F label="Redemption / Coupon Amount">{money(payoff || s.repoAmount)}</F>
      <p style={{ marginTop: 8 }}>Upon redemption/maturity of the above U.S. Treasury security, the Holder directs and assigns the redemption proceeds, in the amount of {money(payoff || s.repoAmount)}, to be paid <strong>in favor of {dash(creditor)}</strong> by Fedwire or certified/official bank check, for credit to the Borrower's Loan / Account No. {dash(acct)} in payoff or reinstatement — or, where applicable, in favor of the buyer/purchaser under the governing purchase &amp; sale agreement.</p>
      <div className="tr-sig" style={{ border: "1px solid #0B4636", background: "#F7F5EC", padding: "8px 10px" }}>
        <p style={{ fontWeight: 700 }}>PAY TO: {dash(creditor)}</p>
        <p>FOR CREDIT TO: {dash(borrower)} · Acct {dash(acct)}</p>
        <p>CUSIP {dash(s.cusip)} · {dash(secLabel)} · AMOUNT {money(payoff || s.repoAmount)}</p>
        <p style={{ fontSize: "9pt", color: "#5C5C5C" }}>Authorized by SEUTRADE Financial Services on the Execution &amp; Signatures page.</p>
      </div>
      <p style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 8 }}>This payment direction is not a discharge by delivery; the obligation is satisfied or reinstated only upon the Counterparty's actual receipt and crediting of redemption proceeds. Not legal advice.</p>

      {/* Execution & Signatures — consolidated final page */}
      <div className="tr-exh" style={{ pageBreakBefore: "always" }}><H2>Execution &amp; Signatures</H2></div>
      <p>The foregoing Master Repurchase Agreement, together with all Exhibits (A through J), is duly executed by the parties as of {fmtDate(s.execDate)}. Each signatory represents that they are authorized to execute this Agreement on behalf of the party indicated.</p>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>SEUTRADE FINANCIAL SERVICES — Seller / Pledgor / Authorized Intermediary</p>
        <p>Signature: <CursiveSig name={firmSig} /></p>
        <p>By: {dash(s.repName)}, {dash(s.repTitle)}</p>
        <p>{FIRM.name} · EIN {FIRM.ein} · {dash(s.repEmail)}</p>
        <p>Date: {fmtDate(s.execDate)}</p>
      </div>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>Borrower / Obligor</p>
        <p>Signature: <CursiveSig name={bSig} /></p>
        <p>{dash(borrower)} — Borrower / Obligor · Loan / Account No. {dash(acct)} ({loanType})</p>
        <p>Executed {execLine(s.execDate)}.</p>
      </div>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>Buyer / Repo Counterparty (SIFMA MRA)</p>
        <p>Name: {dash(s.counterpartySigner)}</p>
        <p>Title: {dash(s.counterpartyTitle)}</p>
        <p>Institution: {dash(s.repoCounterparty)}</p>
        <p>Signature: <CursiveSig name={s.counterpartySigner ? `/s/ ${s.counterpartySigner}` : ""} />  ·  Date: {fmtDate(s.execDate)}</p>
      </div>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>Creditor / Servicer — Acknowledgment (Exhibit G)</p>
        <p>Name: {dash(s.creditorSigner)}</p>
        <p>Title: {dash(s.creditorTitle)}</p>
        <p>Institution: {dash(creditor)}</p>
        <p>Signature: <CursiveSig name={s.creditorSigner ? `/s/ ${s.creditorSigner}` : ""} />  ·  Date: {fmtDate(s.execDate)}</p>
      </div>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>Secured Party / Beneficiary — TCMM Collateral Pledge (Exhibit I)</p>
        <p>Signature: <CursiveSig name={s.creditorSigner ? `/s/ ${s.creditorSigner}` : ""} /> (Authorized Officer, {dash(creditor)})</p>
        <p>Date: {fmtDate(s.execDate)}</p>
      </div>

      <div className="tr-sig" style={{ marginTop: 16 }}>
        <p style={{ fontWeight: 700 }}>Certifying Officer — FS Form 1010 (Exhibit H)</p>
        <p>Signature: <CursiveSig name={s.certifyingOfficer ? `/s/ ${s.certifyingOfficer}` : ""} /> ({FIRM.name} — Corporate Secretary)</p>
        <p>Date: {fmtDate(s.execDate)}  ·  Affix corporate seal: [ SEAL ]</p>
      </div>

      <div className="tr-ecert" style={{ marginTop: 22, border: "1.5px solid #14204a", borderRadius: 6, padding: "12px 14px", background: "#f5f7fb" }}>
        <p style={{ fontWeight: 700, letterSpacing: ".06em", marginBottom: 5, color: "#14204a" }}>ELECTRONIC EXECUTION CERTIFICATE · CORPORATE BANKING RECORD</p>
        <p style={{ fontSize: "9pt", margin: "2px 0" }}>This instrument was executed electronically pursuant to the U.S. ESIGN Act (15 U.S.C. § 7001) and the Uniform Electronic Transactions Act (UETA). SEUTRADE Financial Services and the Borrower adopted their electronic signatures above as legally binding original signatures.</p>
        <div className="tr-f"><span className="tr-fl">Transaction Reference</span><span className="tr-fv">{dash(s.txnRef)}</span></div>
        <div className="tr-f"><span className="tr-fl">Executed — Time / Date Stamp</span><span className="tr-fv">{execStamp ? `${new Date(execStamp).toISOString().replace("T", " ").replace(/\..+/, "")} UTC` : "Pending — stamped automatically upon execution (Save to File)"}</span></div>
        <div className="tr-f"><span className="tr-fl">Signatory (Seller)</span><span className="tr-fv">{dash(s.repName)}, {dash(s.repTitle)} · {FIRM.name}</span></div>
        <div className="tr-f"><span className="tr-fl">Signatory (Borrower)</span><span className="tr-fv">{dash(borrower)}</span></div>
        <p style={{ fontSize: "8.5pt", margin: "6px 0 2px", fontWeight: 700 }}>Document Integrity Hash (SHA-256):</p>
        <p style={{ fontSize: "9pt", margin: 0, fontFamily: "'Courier New', monospace", wordBreak: "break-all", color: "#14204a" }}>{execHash}</p>
        <p style={{ fontSize: "8pt", color: "#5C5C5C", marginTop: 5 }}>Tamper-evident digest of the executed terms (reference, parties, par/purchase/repurchase amounts, rate, account and execution timestamp). Any alteration of the executed terms yields a different hash.</p>
      </div>

    </div>
  );
}

export const treasuryDefaults = () => ({
    txnRef: `STF-TR-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    execDate: todayISO(), valuationDate: todayISO(), tradeDate: todayISO(), settlementDate: todayISO(),
    // Funding instrument (Seutrade note)
    instrumentType: "Secured Negotiable Instrument", instrumentName: "SEUTRADE Credit Enhancement Note",
    instrumentRef: "", instrumentIssuer: FIRM.name, instrumentHolder: FIRM.name, instrumentFace: "",
    instrumentIssueDate: "", instrumentMaturity: "",
    // Treasury security (from picker)
    securityType: "", securityTerm: "", cusip: "", reopening: "No", parAmount: "", purchasePrice: "",
    marketValue: "", interestRate: "", highYield: "", securityMaturity: "",
    custodian: "Federal Reserve Bank of New York (book-entry / FedwireSecurities)", securitiesAccount: "", controlAgreement: "",
    // Repo
    repoCounterparty: "", institutionType: "Primary Dealer / Eligible Bank", masterRepoAgreement: "SIFMA Master Repurchase Agreement (1996/2016)",
    custodianClearing: "", settlementPlatform: "Fedwire Securities Service", repoMaturity: "", repoAmount: "", repoRate: "",
    haircut: "2%", collateralValue: "", repurchaseAmount: "",
    // Fiscal agent / submission
    fiscalAgent: "Federal Reserve Bank of St. Louis — Treasury Support Center (Fiscal Agent of the United States)",
    program: "Treasury Collateral Management & Monitoring (TCMM)", applicableRegulation: "31 CFR Part 380 / 12 CFR Part 210",
    pledgee: "",
    // Settlement / proceeds
    settlementAccount: "", accountBank: "", escrowAgent: "", authorizedDisbursement: FIRM.name,
    proceedsApplied: "", remainingBalance: "0", settlementMechanism: "", releaseRef: "", lienReleaseRef: "",
    participationHolder: FIRM.name, participationAmount: "", participationPct: "100",
    // Rep / execution
    repName: FIRM.officer, repTitle: "Authorized Representative · Treasury Desk", repEmail: "seutradeinfo@gmail.com", repPhone: "",
    acct: {},
});

export default function TreasuryRepo() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auctions, setAuctions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [shared, setShared] = useState(treasuryDefaults);
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));
  const setAcct = (id) => (v) => setShared((p) => ({ ...p, acct: { ...p.acct, [id]: v } }));

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((x) => x.trim()).filter(Boolean) : (appId ? [appId] : []);
    (async () => {
      const results = [];
      for (const id of ids) {
        try { const r = await axios.get(`${API}/borrower/applications/${id}`, cfg); if (r.data.application) results.push(r.data.application); } catch { /* skip */ }
      }
      if (results[0]) {
        const savedSettings = (results.find((a) => a.treasury_doc_settings) || {}).treasury_doc_settings || null;
        const acctMap = {};
        results.forEach((a) => { const v = a.loan_account_no || a.account_no || a.loan_number || ""; if (v) acctMap[a.id] = v; });
        setShared((p) => ({ ...p, ...(savedSettings || {}), acct: { ...acctMap, ...(savedSettings && savedSettings.acct ? savedSettings.acct : {}), ...p.acct } }));
      }
      setApps(results);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [appId, idsParam]);

  const loadAuctions = async () => {
    try {
      const r = await axios.get(`${API}/admin/treasury/auctions?days=180&limit=250`, cfg);
      const list = r.data.securities || [];
      setAuctions(list);
      // Auto-select the most recent auctioned security so the doc populates on open.
      setShared((p) => {
        if (p.cusip || !list.length) return p;
        const sec = list[0];
        return { ...p, cusip: sec.cusip, securityType: sec.securityType, securityTerm: sec.securityTerm, reopening: sec.reopening || "No", interestRate: sec.interestRate || "", highYield: sec.highYield || sec.highDiscountRate || "", securityMaturity: sec.maturityDate || "" };
      });
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAuctions(); /* eslint-disable-next-line */ }, []);

  const pickSecurity = (cusip) => {
    const sec = auctions.find((a) => a.cusip === cusip);
    if (!sec) return;
    setShared((p) => ({
      ...p,
      cusip: sec.cusip, securityType: sec.securityType, securityTerm: sec.securityTerm,
      reopening: sec.reopening || "No", interestRate: sec.interestRate || "",
      highYield: sec.highYield || sec.highDiscountRate || "",
      securityMaturity: sec.maturityDate || "",
    }));
  };

  const saveDocData = async () => {
    if (!apps.length) return;
    const stampedAt = new Date().toISOString();
    setShared((p) => ({ ...p, executedAt: stampedAt }));
    const { acct, ...rest } = shared;
    const settings = { ...rest, executedAt: stampedAt };
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { loan_account_no: (shared.acct && shared.acct[a.id] !== undefined) ? shared.acct[a.id] : acctOf(a, shared), treasury_doc_settings: settings }, cfg)));
      setSavedMsg("Saved & execution-stamped ✓"); setTimeout(() => setSavedMsg(""), 4000);
    } catch { setSavedMsg("Save failed"); setTimeout(() => setSavedMsg(""), 4000); }
    finally { setSaving(false); }
  };

  const [dlBusy, setDlBusy] = useState(false);
  const downloadPdf = async () => {
    if (!apps.length) return;
    setDlBusy(true);
    try {
      const r = await axios.post(`${API}/admin/treasury/bulk-pdf`, { app_ids: apps.map((a) => a.id) }, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = apps.length > 1 ? `seutrade_treasury_packages_${apps.length}.pdf` : "seutrade_treasury_package.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || "Could not build the merged PDF."); }
    finally { setDlBusy(false); }
  };

  const [faxOpen, setFaxOpen] = useState(false);
  const [faxDir, setFaxDir] = useState([]);
  const [faxTo, setFaxTo] = useState(FIRM.tcmm.fax);
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxMsg, setFaxMsg] = useState(null);
  const [faxStatuses, setFaxStatuses] = useState({});
  const [lastFaxId, setLastFaxId] = useState("");
  const openCertificate = async (faxId) => {
    try {
      const r = await axios.get(`${API}/admin/fax/certificate/${faxId}`, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { alert(e.response?.data?.detail || "Certificate not available yet."); }
  };
  useEffect(() => {
    if (!faxOpen || faxDir.length) return;
    axios.get(`${API}/admin/fax/directory`, cfg).then((r) => {
      const flat = (r.data.directory || r.data.groups || []).flatMap((g) => (g.entries || []).map((e) => ({ ...e, category: g.category })));
      setFaxDir(flat);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [faxOpen]);

  const STATUS_UI = {
    delivered: { c: "#3DBB8E", t: "Delivered ✓" }, sent: { c: "#3DBB8E", t: "Sent ✓" }, success: { c: "#3DBB8E", t: "Delivered ✓" },
    sending: { c: "#E4C97A", t: "Sending…" }, queued: { c: "#E4C97A", t: "Queued…" },
    failed: { c: "#FF8585", t: "Failed" }, delivery_failed: { c: "#FF8585", t: "Failed" }, expired: { c: "#FF8585", t: "Expired" },
  };
  const loadFaxStatuses = async () => {
    if (!apps.length) return;
    try {
      const r = await axios.get(`${API}/admin/treasury/fax-status?ids=${apps.map((a) => a.id).join(",")}`, cfg);
      setFaxStatuses(r.data.statuses || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { if (apps.length) loadFaxStatuses(); /* eslint-disable-next-line */ }, [apps]);

  const sendFax = async () => {
    if (!apps.length || !faxTo.trim()) return;
    setFaxBusy(true); setFaxMsg(null);
    try {
      const r = await axios.post(`${API}/admin/treasury/efax`, { app_ids: apps.map((a) => a.id), to: faxTo.trim(), label: `SEUTRADE Treasury Repo Package · ${apps.length} borrower(s)` }, cfg);
      const fid = r.data?.fax?.fax_id || r.data?.fax?.id || "";
      const st = r.data?.fax?.status || "queued";
      setLastFaxId(fid);
      setFaxMsg({ ok: true, status: st, text: `✓ Fax submitted to ${faxTo.trim()} — ${apps.length} package(s). Telnyx ID: ${fid || "n/a"} · Status: ${st}` });
      await loadFaxStatuses();
      // Poll the true delivery status a few times.
      if (fid) {
        let tries = 0;
        const iv = setInterval(async () => {
          tries += 1;
          try {
            const rr = await axios.post(`${API}/admin/fax/${fid}/refresh`, {}, cfg);
            const s = (rr.data.status || "").toLowerCase();
            const done = ["delivered", "sent", "success", "failed", "delivery_failed", "expired"].includes(s);
            const label = done ? s : (tries >= 12 ? `${s || "sending"} · awaiting Telnyx delivery confirmation (see Fax Jobs log)` : s);
            setFaxMsg((m) => m ? { ...m, status: s, text: `✓ Fax to ${faxTo.trim()} — ${apps.length} package(s). Telnyx ID: ${fid} · Status: ${label}${rr.data.failure_reason ? " (" + rr.data.failure_reason + ")" : ""}` } : m);
            await loadFaxStatuses();
            if (done || tries >= 12) clearInterval(iv);
          } catch { if (tries >= 12) clearInterval(iv); }
        }, 5000);
      }
    } catch (e) { setFaxMsg({ ok: false, text: e.response?.data?.detail || "Fax failed to send." }); }
    finally { setFaxBusy(false); }
  };

  return (
    <div style={{ background: "#0A0A0A", minHeight: "100vh" }}>
      <div className="no-print" style={{ position: "sticky", top: 0, zIndex: 20, background: "#0C110F", borderBottom: "1px solid #1F2A25" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "14px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <Link to="/workbench" style={{ color: "#8A8A8A", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}><ArrowLeft size={14} /> Back</Link>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#5FBFA0", fontWeight: 700, letterSpacing: ".08em", fontSize: 13 }}>
              <Landmark size={16} /> TREASURY REPO &amp; COLLATERAL FINANCING {apps.length > 1 ? `· BULK (${apps.length})` : ""}
            </div>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="tr-save" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#5FBFA0", border: "1px solid #2E6F5E", padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
              <Save size={14} /> {saving ? "Saving…" : "Save to file"}
            </button>
            {savedMsg && <span data-testid="tr-saved-msg" style={{ color: savedMsg.includes("fail") ? "#FF8585" : "#3DBB8E", fontSize: 11, fontWeight: 700 }}>{savedMsg}</span>}
            <button onClick={downloadPdf} disabled={dlBusy || !apps.length} data-testid="tr-download" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#9DB4E8", border: "1px solid #5C6B9E", padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer", opacity: dlBusy ? 0.6 : 1 }}>
              <Download size={14} /> {dlBusy ? "Building…" : (apps.length > 1 ? "Merged PDF" : "Download PDF")}
            </button>
            <button onClick={() => setFaxOpen((v) => !v)} disabled={!apps.length} data-testid="tr-efax-toggle" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "#E4C97A", border: "1px solid #B8860B", padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" }}>
              <Send size={14} /> eFax
            </button>
            <button onClick={() => window.print()} data-testid="tr-print" style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#2E6F5E", color: "#F2FBF7", border: "none", padding: "9px 16px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer" }}>
              <Printer size={14} /> Print / Save as PDF
            </button>
          </div>

          {faxOpen && (
            <div className="mb-3 rounded-md border border-[#B8860B]/40 bg-[#12100A] p-3" data-testid="tr-efax-panel">
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#E4C97A] mb-2 flex items-center gap-2"><Send size={12} /> eFax Treasury package{apps.length > 1 ? ` (all ${apps.length} borrowers merged)` : ""}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Destination (directory)</label>
                  <select onChange={(e) => e.target.value && setFaxTo(e.target.value)} data-testid="tr-efax-dir"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]">
                    <option value="">Choose a saved fax destination…</option>
                    <option value={FIRM.tcmm.fax}>★ TCMM — {FIRM.tcmm.agent} · {FIRM.tcmm.fax}</option>
                    {faxDir.map((e) => <option key={e.id} value={e.fax}>{e.category}: {e.label} · {e.fax}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Fax number</label>
                  <input value={faxTo} onChange={(e) => setFaxTo(e.target.value)} data-testid="tr-efax-to" placeholder="+1 866 707 6574"
                    className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
                </div>
                <button onClick={sendFax} disabled={faxBusy || !faxTo.trim()} data-testid="tr-efax-send"
                  className="inline-flex items-center justify-center gap-2 bg-[#B8860B] text-[#0A0A0A] font-bold text-[11px] uppercase tracking-[0.1em] px-4 py-2.5 rounded-md disabled:opacity-60">
                  <Send size={13} /> {faxBusy ? "Sending…" : "Send eFax"}
                </button>
              </div>
              <div className="text-[10px] text-[#8A8A8A] mt-2">Submission destination: <strong className="text-[#E4C97A]">{FIRM.tcmm.name}</strong> · {FIRM.tcmm.agent} · Fax {FIRM.tcmm.fax} · Ph {FIRM.tcmm.phone} · {FIRM.tcmm.email}</div>
              {faxMsg && <div data-testid="tr-efax-msg" className={`text-[12px] font-bold mt-2 ${faxMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{faxMsg.text}
                {lastFaxId && ["delivered", "sent", "success"].includes((faxMsg.status || "").toLowerCase()) && (
                  <button onClick={() => openCertificate(lastFaxId)} data-testid="tr-efax-cert" className="ml-2 underline text-[#5FE0B0]">View Transmission Certificate ↗</button>
                )}</div>}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1 flex items-center gap-2">
                Treasury security (live TreasuryDirect auctions)
                <button onClick={loadAuctions} data-testid="tr-refresh-auctions" className="text-[#5FBFA0]"><RefreshCw size={11} /></button>
              </label>
              <select value={shared.cusip} onChange={(e) => pickSecurity(e.target.value)} data-testid="tr-security-select"
                className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]">
                <option value="">{auctions.length ? `Select from ${auctions.length} recent auctions…` : "Loading auctions…"}</option>
                {auctions.map((a) => (
                  <option key={a.cusip} value={a.cusip}>
                    {a.cusip} · {a.securityType} {a.securityTerm}{a.reopening === "Yes" ? " (Reopening)" : ""} · mat {a.maturityDate} · {a.interestRate ? a.interestRate + "%" : (a.highYield || a.highDiscountRate || "") + " yld"}
                  </option>
                ))}
              </select>
            </div>
            <Ctl label="CUSIP" value={shared.cusip} onChange={set("cusip")} />
            <Ctl label="Par Amount ($)" value={shared.parAmount} onChange={set("parAmount")} type="number" />
            <Ctl label="Purchase Price ($)" value={shared.purchasePrice} onChange={set("purchasePrice")} type="number" />
            <Ctl label="Market Value ($)" value={shared.marketValue} onChange={set("marketValue")} type="number" />
            <Ctl label="Collateral Value ($)" value={shared.collateralValue} onChange={set("collateralValue")} type="number" />
            <Ctl label="Repo Amount / Advance ($)" value={shared.repoAmount} onChange={set("repoAmount")} type="number" />
            <Ctl label="Repurchase Amount ($)" value={shared.repurchaseAmount} onChange={set("repurchaseAmount")} type="number" />
            <Ctl label="Repo Counterparty" value={shared.repoCounterparty} onChange={set("repoCounterparty")} />
            <Ctl label="Master Repo Agreement" value={shared.masterRepoAgreement} onChange={set("masterRepoAgreement")} />
            <Ctl label="Custodian / Clearing Bank" value={shared.custodianClearing} onChange={set("custodianClearing")} />
            <Ctl label="Repo Rate" value={shared.repoRate} onChange={set("repoRate")} />
            <Ctl label="Haircut / Margin" value={shared.haircut} onChange={set("haircut")} />
            <Ctl label="Trade Date" value={shared.tradeDate} onChange={set("tradeDate")} type="date" />
            <Ctl label="Repurchase (Maturity) Date" value={shared.repoMaturity} onChange={set("repoMaturity")} type="date" />
            <Ctl label="Fiscal Agent (Federal Reserve Bank)" value={shared.fiscalAgent} onChange={set("fiscalAgent")} />
            <Ctl label="Securities / Collateral Account" value={shared.securitiesAccount} onChange={set("securitiesAccount")} />
            <Ctl label="Settlement Account" value={shared.settlementAccount} onChange={set("settlementAccount")} />
            <Ctl label="Account Bank" value={shared.accountBank} onChange={set("accountBank")} />
            <Ctl label="Funding Note Face ($)" value={shared.instrumentFace} onChange={set("instrumentFace")} type="number" />
            <Ctl label="Transaction Reference" value={shared.txnRef} onChange={set("txnRef")} />
            <Ctl label="Authorized Rep" value={shared.repName} onChange={set("repName")} />
            <Ctl label="Execution date" value={shared.execDate} onChange={set("execDate")} type="date" />
          </div>

          {apps.length > 0 && (
            <div className="mt-3 border-t border-[#1F2A25] pt-3" data-testid="tr-acct-editor">
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-2">Loan / Account No. {apps.length > 1 ? "(per borrower)" : ""} — populates every document</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {apps.map((a) => (
                  <div key={a.id} className="flex items-center gap-2">
                    <span className="text-[11px] text-[#9A9A90] min-w-[120px] truncate">{a.borrower_full_name || a.borrower_name || a.id}</span>
                    <input value={acctOf(a, shared)} onChange={(e) => setAcct(a.id)(e.target.value)} data-testid={`tr-acct-${a.id}`}
                      placeholder="Enter loan / account no."
                      className="flex-1 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]" />
                    {faxStatuses[a.id] && (() => { const s = (faxStatuses[a.id].status || "").toLowerCase(); const ui = STATUS_UI[s] || { c: "#9A9A90", t: s || "—" }; return (
                      <span data-testid={`tr-faxchip-${a.id}`} title={`Fax ${faxStatuses[a.id].fax_id} → ${faxStatuses[a.id].to || ""}`} className="text-[10px] font-bold px-2 py-1 rounded-sm whitespace-nowrap" style={{ color: ui.c, border: `1px solid ${ui.c}55` }}>eFax: {ui.t}</span>
                    ); })()}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10.5px] text-[#8A8A8A] mt-2">Treasury security data is pulled live from the public TreasuryDirect Auction Query API (real CUSIPs &amp; reopenings). Fields left blank print as fillable blanks; borrower/loan data auto-populates from each file.</div>
        </div>
      </div>

      <div className="cm-print-area" style={{ maxWidth: 920, margin: "0 auto", padding: "28px 0" }}>
        {loading ? (
          <div style={{ color: "#8A8A8A", textAlign: "center", padding: 60 }}>Loading…</div>
        ) : apps.length === 0 ? (
          <div style={{ color: "#8A8A8A", textAlign: "center", padding: 60 }} data-testid="tr-empty">No application loaded. Open this from a borrower file, or pass ?ids=… for bulk.</div>
        ) : (
          apps.map((a, i) => <TreasuryDoc key={a.id} app={a} shared={shared} last={i === apps.length - 1} />)
        )}
      </div>

      <style>{TREASURY_CSS}</style>
    </div>
  );
}
